import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Radio,
  RadioGroup,
  Box,
  Typography,
  IconButton,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';

export interface SaveAsNewWorkflowDialogProps {
  open: boolean;
  onConfirm: (data: {
    fileType: string;
    name: string;
    folderOption: 'existing' | 'new';
    folderName?: string;
    folderPath?: string;
  }) => void;
  onCancel: () => void;
  onSelectFolder: (forPath?: boolean) => void;
  selectedFolderName?: string;
  selectedFolderPath?: string;
}

export const SaveAsNewWorkflowDialog: React.FC<SaveAsNewWorkflowDialogProps> = ({
  open,
  onConfirm,
  onCancel,
  onSelectFolder,
  selectedFolderName,
  selectedFolderPath,
}) => {
  const [workflowName, setWorkflowName] = useState('');
  const [folderName, setFolderName] = useState('');
  const [folderOption, setFolderOption] = useState<'existing' | 'new' | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  // When dialog closes, reset form
  useEffect(() => {
    if (!open) {
      setWorkflowName('');
      setFolderName('');
      setFolderOption(null);
      setSubmitted(false);
      setIsClosing(false);
    }
  }, [open]);

  const handleClose = useCallback(() => {
    setIsClosing(true);
    onCancel();
  }, [onCancel]);

  const handleConfirm = useCallback(() => {
    setSubmitted(true);

    // Validation
    if (!workflowName.trim()) return;
    if (!folderOption) return;
    if (folderOption === 'existing' && !selectedFolderName) return;
    if (folderOption === 'new' && !selectedFolderName) return;
    if (folderOption === 'new' && !folderName.trim()) return;

    // Close dialog immediately (before parent state update takes effect)
    setIsClosing(true);
    
    // Call parent callback
    onConfirm({
      fileType: 'WETLABWORKFLOW',
      name: workflowName,
      folderOption,
      folderName: folderOption === 'new' ? folderName : undefined,
      folderPath: selectedFolderPath || undefined,
    });
  }, [workflowName, folderName, folderOption, selectedFolderPath, selectedFolderName, onConfirm]);

  return (
    <Dialog
      open={open && !isClosing}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: '#f4f6fc',
          color: '#1a4671',
          fontWeight: 600,
          fontSize: '1rem',
          borderBottom: '1px solid #e0e6f2',
          position: 'relative',
        }}
      >
        Create File
        <IconButton
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: '#666',
            '&:hover': {
              backgroundColor: '#f0f0f0',
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {/* Choose File Type */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 500, minWidth: '140px', whiteSpace: 'nowrap' }}>
              Choose File Type <span style={{ color: '#dc3545' }}>*</span>
            </Typography>
            <Typography
              sx={{
                flex: 1,
                fontSize: '0.9rem',
                color: '#1a4671',
                p: 1,
                backgroundColor: '#f9fafb',
                borderRadius: 0.5,
                border: '1px solid #d1d5db',
                minHeight: '36px',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              Wet-lab Procedure
            </Typography>
          </Box>

          {/* Name */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 500, minWidth: '140px', whiteSpace: 'nowrap' }}>
              Name <span style={{ color: '#dc3545' }}>*</span>
            </Typography>
            <TextField
              size="small"
              placeholder="Enter name"
              value={workflowName}
              onChange={(e) => setWorkflowName(e.target.value)}
              error={submitted && !workflowName.trim()}
              helperText={submitted && !workflowName.trim() ? 'Name is required' : ''}
              sx={{
                flex: 1,
                minWidth: 0,
                '& .MuiOutlinedInput-root': {
                  fontSize: '0.9rem',
                },
              }}
            />
          </Box>

          {/* Choose Folder */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 500, minWidth: '140px', whiteSpace: 'nowrap', pt: 0.5 }}>
              Choose Folder <span style={{ color: '#dc3545' }}>*</span>
            </Typography>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                <FormControlLabel
                  value="existing"
                  control={
                    <Radio
                      size="small"
                      checked={folderOption === 'existing'}
                    />
                  }
                  label="Add to existing Folder"
                  onClick={() => {
                    setFolderOption('existing');
                    onSelectFolder(false);
                  }}
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      fontSize: '0.9rem',
                    },
                  }}
                />
                <FormControlLabel
                  value="new"
                  control={
                    <Radio
                      size="small"
                      checked={folderOption === 'new'}
                      onChange={() => {
                        setFolderOption('new');
                      }}
                    />
                  }
                  label="Create New Folder"
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      fontSize: '0.9rem',
                    },
                  }}
                />
              </Box>
              {submitted && !folderOption && (
                <Typography sx={{ color: '#dc3545', fontSize: '0.8rem' }}>
                  Please select a folder option
                </Typography>
              )}
            </Box>
          </Box>

          {/* Add to Existing Folder Section */}
          {folderOption === 'existing' && (
            <>
              <Box 
                sx={{ display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer' }}
                onClick={() => onSelectFolder(false)}
              >
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 500, minWidth: '140px', whiteSpace: 'nowrap' }}>
                  Selected Folder <span style={{ color: '#dc3545' }}>*</span>
                </Typography>
                <Typography
                  sx={{
                    flex: 1,
                    fontSize: '0.9rem',
                    color: selectedFolderName ? '#1a4671' : '#999999',
                    '&:hover': {
                      textDecoration: 'underline',
                    }
                  }}
                >
                  {selectedFolderName || 'No folder selected'}
                </Typography>
              </Box>
              {submitted && !selectedFolderName && (
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box sx={{ minWidth: '140px' }} />
                  <Typography sx={{ color: '#dc3545', fontSize: '0.8rem' }}>
                    Please select a folder
                  </Typography>
                </Box>
              )}
            </>
          )}

          {/* Create New Folder Section */}
          {folderOption === 'new' && (
            <>
              {/* Folder Name */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 500, minWidth: '140px', whiteSpace: 'nowrap' }}>
                  Folder Name <span style={{ color: '#dc3545' }}>*</span>
                </Typography>
                <TextField
                  size="small"
                  placeholder="Enter folder name"
                  value={folderName}
                  onChange={(e) => setFolderName(e.target.value)}
                  error={submitted && !folderName.trim()}
                  helperText={submitted && !folderName.trim() ? 'Folder name is required' : ''}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    '& .MuiOutlinedInput-root': {
                      fontSize: '0.9rem',
                    },
                  }}
                />
              </Box>

              {/* Choose Path */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 500, minWidth: '140px', whiteSpace: 'nowrap' }}>
                  Choose Path <span style={{ color: '#dc3545' }}>*</span>
                </Typography>
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  {selectedFolderName && (
                    <Typography
                      sx={{
                        flex: 1,
                        fontSize: '0.9rem',
                        color: '#1a4671',
                      }}
                    >
                      {selectedFolderName}
                    </Typography>
                  )}
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => onSelectFolder(false)}
                    sx={{
                      textTransform: 'none',
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Choose Storage Location
                  </Button>
                </Box>
              </Box>
              {submitted && !selectedFolderName && (
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box sx={{ minWidth: '140px' }} />
                  <Typography sx={{ color: '#dc3545', fontSize: '0.8rem' }}>
                    Please choose a parent storage location
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          backgroundColor: '#f9fafb',
          borderTop: '1px solid #e5e7eb',
          px: 3,
          py: 2,
          gap: 1,
          justifyContent: 'flex-end',
        }}
      >
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          startIcon={<SaveIcon />}
          sx={{
            textTransform: 'none',
            fontSize: '0.9rem',
            fontWeight: 500,
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

